import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Package, Play, Eye, RefreshCw } from 'lucide-react';
import { getOrders, markOrderReady, Order, OrderStatus } from '../services/api';
import { formatDistanceToNow } from 'date-fns';

const statusFilters: { label: string; value: OrderStatus | 'ALL' }[] = [
  { label: 'All', value: 'ALL' },
  { label: 'Pending', value: 'PENDING' },
  { label: 'Quote Sent', value: 'QUOTE_SENT' },
  { label: 'In Delivery', value: 'IN_DELIVERY' },
  { label: 'Delivered', value: 'DELIVERED' },
];

function StatusBadge({ status }: { status: string }) {
  const badgeClass = {
    PENDING: 'badge-pending',
    READY: 'badge-ready',
    QUOTE_SENT: 'badge-ready',
    QUOTE_APPROVED: 'badge-in-delivery',
    IN_DELIVERY: 'badge-in-delivery',
    DELIVERED: 'badge-delivered',
    CANCELLED: 'badge-cancelled',
    FAILED: 'badge-failed',
  }[status] || 'badge-pending';

  return <span className={`badge ${badgeClass}`}>{status.replace(/_/g, ' ')}</span>;
}

export default function Orders() {
  const [statusFilter, setStatusFilter] = useState<OrderStatus | 'ALL'>('ALL');
  const queryClient = useQueryClient();

  const { data: orders = [], isLoading, refetch } = useQuery({
    queryKey: ['orders', statusFilter],
    queryFn: () =>
      getOrders(statusFilter === 'ALL' ? undefined : { status: statusFilter }),
  });

  const markReadyMutation = useMutation({
    mutationFn: markOrderReady,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  const handleMarkReady = async (orderId: string) => {
    if (confirm('Mark this order as ready and get a delivery quote?')) {
      try {
        const result = await markReadyMutation.mutateAsync(orderId);
        alert(
          `Quote sent! Price: $${(result.quote.priceAmount / 100).toFixed(2)}`
        );
      } catch {
        alert('Failed to get quote. Please try again.');
      }
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">Orders</h2>
        <button
          onClick={() => refetch()}
          className="btn-secondary flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {statusFilters.map((filter) => (
          <button
            key={filter.value}
            onClick={() => setStatusFilter(filter.value)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              statusFilter === filter.value
                ? 'bg-uber-green text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {filter.label}
          </button>
        ))}
      </div>

      {/* Orders Table */}
      <div className="card">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-uber-green"></div>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No orders found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-sm text-gray-500 border-b">
                  <th className="pb-3 font-medium">Order</th>
                  <th className="pb-3 font-medium">Customer</th>
                  <th className="pb-3 font-medium">Address</th>
                  <th className="pb-3 font-medium">Status</th>
                  <th className="pb-3 font-medium">Created</th>
                  <th className="pb-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order: Order) => (
                  <tr key={order.id} className="border-b last:border-0">
                    <td className="py-4 font-medium">
                      {order.shopifyOrderNumber}
                    </td>
                    <td className="py-4">
                      <div>{order.customerName}</div>
                      <div className="text-sm text-gray-500">
                        {order.customerPhone}
                      </div>
                    </td>
                    <td className="py-4 text-sm text-gray-600">
                      {order.deliveryAddress}
                      <br />
                      {order.deliveryCity}, {order.deliveryState}{' '}
                      {order.deliveryZip}
                    </td>
                    <td className="py-4">
                      <StatusBadge status={order.status} />
                    </td>
                    <td className="py-4 text-gray-500">
                      {formatDistanceToNow(new Date(order.createdAt), {
                        addSuffix: true,
                      })}
                    </td>
                    <td className="py-4">
                      <div className="flex items-center gap-2">
                        {order.status === 'PENDING' && (
                          <button
                            onClick={() => handleMarkReady(order.id)}
                            disabled={markReadyMutation.isPending}
                            className="btn-primary text-sm flex items-center gap-1"
                          >
                            <Play className="w-4 h-4" />
                            Ready
                          </button>
                        )}
                        <Link
                          to={`/orders/${order.id}`}
                          className="btn-secondary text-sm flex items-center gap-1"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
